import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus } from "@azure/msal-browser";
import { apiTokenRequest } from "./config";
import { startLogin, startLogout } from "./interaction";
import { resolveLevel, type UserLevel } from "./levels";

export interface AuthState {
  ready: boolean; // MSAL idle (no interaction in flight)
  signedIn: boolean;
  level: UserLevel;
  isAdmin: boolean;
  name?: string;
  email?: string;
  roles: string[];
  login: () => void;
  logout: () => void;
  /** Acquire an access token for the content API scope (silent, with interactive fallback). */
  getApiToken: () => Promise<string | null>;
}

interface IdClaims {
  roles?: string[];
  name?: string;
  preferred_username?: string;
  email?: string;
}

export function useAuth(): AuthState {
  const { instance, accounts, inProgress } = useMsal();
  const signedIn = useIsAuthenticated();
  const account = accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as IdClaims;
  const roles = claims.roles ?? [];
  const level = resolveLevel(signedIn, roles);
  return {
    ready: inProgress === InteractionStatus.None,
    signedIn,
    level,
    isAdmin: level === "admin",
    name: account?.name ?? claims.name,
    email: claims.preferred_username ?? claims.email,
    roles,
    login: () => startLogin(instance),
    logout: () => startLogout(instance),
    getApiToken: async () => {
      if (!account) return null;
      try {
        const r = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
        return r.accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          const r = await instance.acquireTokenPopup(apiTokenRequest);
          return r.accessToken;
        }
        return null;
      }
    },
  };
}
